import { computed, onBeforeUnmount, ref } from 'vue';

import { translate } from '@/shared/i18n';
import { toast } from '@/shared/lib';

const maxMessageAttachmentSize = 100 * 1024 * 1024;

type SelectedMessageAttachment = {
  file: File;
  previewUrl: string | null;
};

const useMessageCreateAttachmentsState = () => {
  const attachments = ref<SelectedMessageAttachment[]>([]);

  const files = computed<File[]>(() => attachments.value.map(({ file }) => file));
  const isAttachmentsExists = computed<boolean>(() => attachments.value.length > 0);

  const addFiles = (filesToAdd: File[]): void => {
    filesToAdd.forEach((file) => {
      if (file.size > maxMessageAttachmentSize) {
        toast.error(translate('chat.attachmentTooLarge', { filename: file.name }));
        return;
      }

      attachments.value.push({
        file,
        previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      });
    });
  };

  const removeAttachment = (index: number): void => {
    const [removed] = attachments.value.splice(index, 1);
    if (removed?.previewUrl) {
      URL.revokeObjectURL(removed.previewUrl);
    }
  };

  const resetAttachments = (): void => {
    attachments.value.forEach((attachment) => {
      if (attachment.previewUrl) {
        URL.revokeObjectURL(attachment.previewUrl);
      }
    });
    attachments.value = [];
  };

  onBeforeUnmount(resetAttachments);

  return {
    attachments,
    files,
    isAttachmentsExists,
    addFiles,
    removeAttachment,
    resetAttachments,
  };
};

export { useMessageCreateAttachmentsState };
